import React, { useCallback, useRef } from "react";

// Map a hex color to a base frequency. Hue-like spread across a calm pentatonic range.
function hexToFreq(hex) {
  if (!hex || typeof hex !== "string") return 220;
  const h = hex.replace("#", "");
  let r = 128, g = 128, b = 128;
  if (h.length === 6) {
    r = parseInt(h.slice(0, 2), 16);
    g = parseInt(h.slice(2, 4), 16);
    b = parseInt(h.slice(4, 6), 16);
  }
  // Pseudo-hue → frequency
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min || 1;
  let hue;
  if (max === r) hue = ((g - b) / d) % 6;
  else if (max === g) hue = (b - r) / d + 2;
  else hue = (r - g) / d + 4;
  if (hue < 0) hue += 6;
  // Range 220 Hz (A3) → 660 Hz (E5) — calm, breathy
  return 220 + (hue / 6) * 440;
}

let _ctx = null;
function getAudioCtx() {
  if (typeof window === "undefined") return null;
  if (_ctx) return _ctx;
  try {
    _ctx = new (window.AudioContext || window.webkitAudioContext)();
  } catch (_e) {
    return null;
  }
  return _ctx;
}

/** Play a slow breath-like tone for a kin. ~2.5s sine with gentle attack/release. */
export function playKinTone(hex) {
  const ctx = getAudioCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume();
  const now = ctx.currentTime;
  const freq = hexToFreq(hex);
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(freq, now);
  osc.frequency.linearRampToValueAtTime(freq * 1.005, now + 1.2); // tiny breath drift
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(0.18, now + 0.4); // soft attack
  gain.gain.setValueAtTime(0.18, now + 1.5);
  gain.gain.linearRampToValueAtTime(0, now + 2.4); // long release
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 2.5);
}

/** Wrap any kin element to make it audibly playable on click. */
export default function AudioSigil({ hex, children, ariaLabel = "play kin tone" }) {
  const lastPlayRef = useRef(0);
  const onClick = useCallback(() => {
    const now = Date.now();
    if (now - lastPlayRef.current < 600) return; // de-spam
    lastPlayRef.current = now;
    playKinTone(hex);
  }, [hex]);
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className="bg-transparent border-0 p-0 cursor-pointer hover:opacity-95 transition-opacity"
      data-testid="audio-sigil-trigger"
      title="click to hear this kin's breath"
    >
      {children}
    </button>
  );
}
